import React from "react";
import { Card, CardBody, Button } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

/**
 * ALERT QTY
 *
 * Spare parts whose available stock has fallen to or below the alert quantity
 * set on the Spare Part Inventory. Two bars per item - what is on hand and the
 * level it should not go under - so the gap is readable without doing any
 * arithmetic.
 */
const AlertQtyChart = ({ items = [], loading = false, source = "alert_qty" }) => {
  const navigate = useNavigate();

  /* Say out loud where the alert level came from, so nobody has to guess why
     an item is on the list. */
  const sourceLabel =
    source === "alert_qty"
      ? "Alert Qty from Spare Part Inventory"
      : "Min Stock Qty from Item Master";

  const labels = items.map(
    (item) => item.gmds_code || item.material_name || item.material_code || "-"
  );

  const series = [
    { name: "Available Qty", data: items.map((i) => i.available_qty) },
    { name: "Alert Level", data: items.map((i) => i.alert_qty) },
  ];

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "55%",
        borderRadius: 3,
        dataLabels: { position: "top" },
      },
    },
    colors: ["#ff3d60", "#adb5bd"],
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    grid: { borderColor: "#f1f1f1", padding: { left: 0, right: 8 } },
    xaxis: {
      categories: labels,
      title: { text: "Quantity", style: { fontWeight: 500 } },
      labels: { formatter: (val) => Math.round(val) },
    },
    yaxis: {
      labels: {
        maxWidth: 130,
        style: { fontSize: "11px" },
      },
    },
    legend: { position: "top", horizontalAlign: "right", markers: { radius: 3 } },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => `${val} qty` },
    },
  };

  /* Grows with the number of bars so they never squeeze together, but stays
     capped so the card does not run past the pending panel beside it. */
  const chartHeight = Math.min(330, Math.max(240, items.length * 30 + 80));

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h5 className="card-title mb-1">Alert Qty</h5>
            <p className="text-muted font-size-12 mb-0">
              Spare parts at or below their alert level
              <span className="d-block font-size-11">{sourceLabel}</span>
            </p>
          </div>

          <Button
            color="link"
            className="p-0 font-size-13 text-decoration-none"
            onClick={() => navigate("/spare-part-inventory-list")}
          >
            View Inventory <i className="mdi mdi-chevron-right" />
          </Button>
        </div>

        {loading ? (
          <div className="dashboard-placeholder">Loading...</div>
        ) : items.length === 0 ? (
          <div className="dashboard-placeholder text-center">
            <i className="ri-checkbox-circle-line font-size-24 text-success d-block mb-2" />
            <span className="text-muted">
              All spare parts are above their alert level.
            </span>
          </div>
        ) : (
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={chartHeight}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default AlertQtyChart;
